import UploadIMG from '@assets/jsx-icon/UploadIMG';
import { IconButton, Skeleton, Stack, styled, Typography } from '@mui/material';
import color from '@theme/Colors';
import React from 'react';
import { AttachmentsFiles, MAX_SIZE_MB } from '../config';
import AttachmentFileItem, { AttachmentFileItemProps } from './AttachmentFileItem';

export interface FormCreateAttachmentsProps extends Partial<AttachmentFileItemProps> {
    uploadedAttachmentsFile: AttachmentsFiles[];
    loadingState: boolean;
    handleChangeAttachmentsFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
}
export default function FormCreateAttachments(props: FormCreateAttachmentsProps) {
    const {
        isEditing,
        loadingState,
        attachmentsFileItem,
        attachmentsDescription,
        uploadedAttachmentsFile,
        popupConfirmDeleteAttachmentsFile,
        onSetAttachmentsFileItem,
        handleKeyboardEvent,
        handleBlurTextField,
        handleDeleteAttachmentsFile,
        handleChangeAttachmentsFile,
        handleClickUpsertDescription,
        handleTypingChangeDescription,
    } = props;

    return (
        <Stack
            width={'100%'}
            bgcolor={'#FFFFFF'}
            borderRadius={'4px'}
            p={'16px 16px 0px'}
            direction={'column'}
            spacing={2}
        >
            <BaseUploadComponent
                helperText={`JPEG, JPG, PNG or WEBP (max. ${MAX_SIZE_MB}Mb)`}
                onChangeFile={handleChangeAttachmentsFile}
            />

            <Stack
                direction={'row'}
                width={'100%'}
                justifyContent={'start'}
                sx={{
                    mt: !uploadedAttachmentsFile?.length ? 0 : '56px !important',
                    flexWrap: 'wrap',
                }}
            >
                {loadingState ? (
                    <Stack direction={'row'} spacing={2} width={'100%'} mb={2} justifyContent={'start'}>
                        {[...new Array(5)].map((_, i) => (
                            <Skeleton
                                animation="pulse"
                                variant="rectangular"
                                width="20%"
                                height={'80px'}
                                sx={{
                                    borderRadius: '4px',
                                    backgroundColor: color.grey200,
                                }}
                            />
                        ))}
                    </Stack>
                ) : (
                    <>
                        {uploadedAttachmentsFile?.length > 0 && (
                            <>
                                {uploadedAttachmentsFile?.map((file, index) => {
                                    const conditionLengthText = file.Desc?.length! > 4 || !file.Desc;
                                    return (
                                        <AttachmentFileItem
                                            fileItem={file}
                                            index={index}
                                            isEditing={isEditing!}
                                            popupConfirmDeleteAttachmentsFile={popupConfirmDeleteAttachmentsFile!}
                                            attachmentsDescription={attachmentsDescription}
                                            attachmentsFileItem={attachmentsFileItem!}
                                            conditionLengthText={conditionLengthText}
                                            onSetAttachmentsFileItem={onSetAttachmentsFileItem!}
                                            handleKeyboardEvent={handleKeyboardEvent!}
                                            handleBlurTextField={handleBlurTextField!}
                                            handleDeleteAttachmentsFile={handleDeleteAttachmentsFile!}
                                            handleClickUpsertDescription={handleClickUpsertDescription!}
                                            handleTypingChangeDescription={handleTypingChangeDescription!}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </>
                )}
            </Stack>
        </Stack>
    );
}

interface BaseUploadComponentProps {
    id?: string;
    borderStyle?: string;
    label?: string;
    isMultiple?: boolean;
    helperText: string;
    onChangeFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const BaseUploadComponent: React.FC<BaseUploadComponentProps> = ({
    id = 'file-upload',
    borderStyle = '1px dashed #ccc',
    helperText,
    isMultiple,
    onChangeFile,
    label = 'Attachments',
}) => {
    return (
        <Stack
            width={'100%'}
            component="label"
            sx={{
                border: borderStyle,
                borderRadius: '4px',
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
            }}
            paddingY={2}
        >
            <Typography
                component="span"
                sx={{
                    position: 'absolute',
                    top: '-10px',
                    left: '7%',
                    transform: 'translateX(-50%)',
                    backgroundColor: color.white,
                    padding: '0 4px',
                    fontSize: '11px',
                    color: color.textSecondary,
                }}
            >
                {label}
            </Typography>
            <IconButton
                component="label"
                sx={{
                    margin: '0 auto',
                    backgroundColor: color.grey200,
                    border: `5px solid ${color.grey100}`,
                    width: '48px',
                    height: '48px',
                }}
            >
                <UploadIMG />
                <VisuallyHiddenInput
                    id={id}
                    type="file"
                    accept="image/*"
                    onChange={onChangeFile}
                    multiple={isMultiple || true}
                />
            </IconButton>
            <Typography>Click to upload</Typography>
            <Typography sx={{ mb: 0 }} variant="body2" color="textSecondary">
                {helperText}
            </Typography>
        </Stack>
    );
};

export const VisuallyHiddenInput = styled('input')({
    display: 'none',
});
